import React, {
  useState,
  createRef,
  useEffect,
  useRef,
  RefObject,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@material-ui/core';
import { Formik, Form, FormikHelpers } from 'formik';

import FormNavigationButtons from 'components/FormNavigationButtons';
import { IExpandedMailingRule } from 'interfaces/MailingRule';
import { FORM_MODE } from 'shared/enums';
import { RootState } from 'store';
import FormikOnError from 'utils/FormikOnError';

import { setSelectedMailingRuleModeAction } from 'modules/mailingRuleModeNavigation';

import { useStyles } from './styles';
import { createMailingSchema } from './validation';
import { steps } from './helpers';
import { getInitialValues } from './initialValues';
import Header from './Header';
import Loading from '../Loading';
import { handleSubmitDraft, handleSubmit } from './helpers';
import { MAILING_RULE_STEPS_IDS } from 'shared/enums';
import { isLoading } from 'utils/loading';
import { listRemunerationBonusAction } from 'modules/remunerationBonusList';
import { listRemunerationCommissionAction } from 'modules/remunerationCommissionList';
import { listRemunerationFixedAction } from 'modules/remunerationFixedList';
import { listRemunerationGoalAction } from 'modules/remunerationGoalList';

const MailingRuleRegister: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { mode } = useSelector(
    (state: RootState) => state.selectedMailingRuleModeNavigation,
  );
  const { loading: mailingRuleLoading, current } = useSelector(
    (state: RootState) => state.mailingRule,
  );
  const isEditable = mode === FORM_MODE.create || mode === FORM_MODE.edit;

  const {
    list: { mailingRuleSystemFields },
    loading: parametersLoading,
  } = useSelector((state: RootState) => state.parameters);

  const [showAccordions, setShowAccordions] = useState<Array<boolean>>(
    Array(steps.length).fill(false),
  );
  const [
    accordionErrorId,
    setAccordionErrorId,
  ] = useState<MAILING_RULE_STEPS_IDS>(MAILING_RULE_STEPS_IDS.NO_STEP);
  const [forceShowError, setForceShowError] = useState<boolean>(false);
  const accordionsRef = useRef<Array<RefObject<HTMLElement>>>([]);

  useEffect(() => {
    accordionsRef.current = Array(steps.length)
      .fill(null)
      .map(() => createRef());
  }, []);

  const { loading: commissionListLoading } = useSelector(
    (state: RootState) => state.remunerationCommissionList,
  );

  const { loading: bonusListLoading } = useSelector(
    (state: RootState) => state.remunerationBonusList,
  );

  const { loading: goalListLoading } = useSelector(
    (state: RootState) => state.remunerationGoalList,
  );

  const { loading: fixedListLoading } = useSelector(
    (state: RootState) => state.remunerationFixedList,
  );

  useEffect(() => {
    dispatch(listRemunerationCommissionAction({ limit: 9999 }));
    dispatch(listRemunerationBonusAction({ limit: 9999 }));
    dispatch(listRemunerationGoalAction({ limit: 9999 }));
    dispatch(listRemunerationFixedAction({ limit: 9999 }));
  }, [dispatch]);

  const submitHandler = (
    values: IExpandedMailingRule,
    formikHelpers: FormikHelpers<IExpandedMailingRule>,
  ) => {
    formikHelpers.setSubmitting(true);
    return handleSubmit(values, formikHelpers.setSubmitting);
  };

  const handleAccordion = (expanded: boolean, id: number) => {
    const newShowAccordions = showAccordions.slice();
    newShowAccordions[id] = expanded;
    setShowAccordions(newShowAccordions);
  };

  const getErrorAccordionId = (errorKey: string) => {
    const accordionList =
      accordionsRef.current?.map((ref) => ref?.current) || [];

    return accordionList.findIndex((accordion) =>
      accordion?.querySelector(`[name="${errorKey}"]`),
    );
  };

  const showAllAccordions = (key: string) => {
    setShowAccordions(Array(steps.length).fill(true));
    setAccordionErrorId(getErrorAccordionId(key));
    setForceShowError(false);
  };

  const initialValuesBySystems = getInitialValues(mailingRuleSystemFields);

  const loading = isLoading([
    mailingRuleLoading,
    parametersLoading,

    commissionListLoading,
    bonusListLoading,
    goalListLoading,
    fixedListLoading,
  ]);

  return (
    <Grid
      container
      direction="column"
      justifyContent="flex-start"
      alignItems="flex-start"
    >
      <Header />
      <Grid item xs={12}>
        {loading ? (
          <Loading />
        ) : (
          <Formik
            initialValues={current || initialValuesBySystems}
            validationSchema={createMailingSchema}
            onSubmit={submitHandler}
            validateOnChange={false}
            enableReinitialize={false}
          >
            {({ setSubmitting, values, errors }) => (
              <FormikOnError
                onError={showAllAccordions}
                forceShow={forceShowError}
              >
                <Form className={classes.form}>
                  <Grid
                    container
                    direction="column"
                    justifyContent="flex-start"
                    alignItems="stretch"
                  >
                    {steps.map(({ label, Component }, index) => (
                      <Accordion
                        data-cy="mailing-rule-register__container--accordion"
                        key={label}
                        ref={accordionsRef?.current[index]}
                        expanded={showAccordions[index]}
                        onChange={(event, expanded) => {
                          handleAccordion(expanded, index);
                        }}
                        TransitionProps={{
                          timeout: Object.keys(errors).length ? 0 : 300,
                          onEntered: () =>
                            accordionErrorId === index &&
                            Object.keys(errors).length &&
                            setForceShowError(true),
                        }}
                      >
                        <AccordionSummary data-cy="mailing-rule-register__accordion--summary">
                          <Typography variant="h5">{`${
                            index + 1
                          } - ${label}`}</Typography>
                        </AccordionSummary>
                        <AccordionDetails data-cy="mailing-rule-register__accordion--details">
                          <Grid item xs={12}>
                            <Component isEditable={isEditable} />
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                    <FormNavigationButtons
                      isViewOnly={!isEditable}
                      backButtonOnClick={() =>
                        dispatch(
                          setSelectedMailingRuleModeAction(FORM_MODE.list),
                        )
                      }
                      submitButtonLabel={
                        mode === FORM_MODE.create || values.status === 'DRAFT'
                          ? 'Cadastrar'
                          : 'Atualizar'
                      }
                      secondarySubmitButtonOnClick={() => {
                        handleSubmitDraft(values, setSubmitting);
                      }}
                      secondarySubmitButtonLabel={
                        mode === FORM_MODE.create || values.status === 'DRAFT'
                          ? 'Salvar Rascunho'
                          : undefined
                      }
                    />
                  </Grid>
                </Form>
              </FormikOnError>
            )}
          </Formik>
        )}
      </Grid>
    </Grid>
  );
};

export default MailingRuleRegister;
